import React from 'react';
import { useLazyQuery } from 'apollo-client-ppc';
import debounce from 'lodash/debounce';
import SearchIcon from '@atlaskit/icon/glyph/search';
import { Field, HelperMessage } from '@atlaskit/form';
import { AsyncSelect, components, InputProps } from '@atlaskit/select';
import Button from '@atlaskit/button';
import { CommerceAddress } from '@css-xp/graphtypes/src/generated-types-new';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { withApollo } from '@css-xp/base';
import { FormatCommerceAddress } from '@css-xp/ppc-common';
import { CheckoutOperation, usePartnerCheckoutNotification } from './hooks/use-place-order-notification';
import { fetchSoldToAddressesForOrderReview } from './queries/fetch-sold-to-addresses';
import './search-sold-to-address.scss';

const FIELD_NAME = 'searchTermForSoldToAddress';
export const DEBOUNCE_WAIT_TIME = 1000;

export type SearchSoldToAddressProps = {
  transactionAccountId: string;
  fieldName?: string;
  onSelectSoldToAddress?: (soldToAddress: CommerceAddress) => void;
  autoSelectedSoldToAddress?: CommerceAddress | undefined | null;
};

const Input = (props: InputProps) => {
  return (
    <div className="ppc-async-search-sold-to-address-custom-input-container">
      <div className="search-sold-to-address-icon-wrapper">
        <SearchIcon label="search-icon" size="medium" />
      </div>
      <components.Input {...props} />
    </div>
  );
};

const startTypingMessage = 'Start typing to see search results';
const NoOptionsMessage = (props: any) => {
  return <components.NoOptionsMessage {...props}> {startTypingMessage} </components.NoOptionsMessage>;
};

const SearchSoldToAddressImpl: React.FC<SearchSoldToAddressProps> = ({
  transactionAccountId,
  fieldName = FIELD_NAME,
  onSelectSoldToAddress,
  autoSelectedSoldToAddress,
}) => {
  const [selectedSoldToAddress, setSelectedSoldToAddress] = React.useState<Partial<CommerceAddress> | null>(null);
  const [isAddressSelected, setIsAddressSelected] = React.useState(false);
  const { notifyFailure } = usePartnerCheckoutNotification();
  const [fetchSoldToAddresses, { loading }] = useLazyQuery<
    generatedTypesNew.FetchSoldToAddressesForOrderReviewQuery,
    generatedTypesNew.FetchSoldToAddressesForOrderReviewQueryVariables
  >(fetchSoldToAddressesForOrderReview, {
    variables: {
      searchParams: { query: '', transactionAccountId },
    },
  });

  const loadSoldToAddressOptions = async (term: string) => {
    if (term) {
      const { data, error } = await fetchSoldToAddresses({
        variables: {
          searchParams: { query: term, transactionAccountId },
        },
      });
      if (error) {
        notifyFailure(
          error.message ?? 'Unable to fetch sold to address right now.',
          CheckoutOperation.SelectSoldToAddress
        );
        return;
      }

      const results = data?.shipToParty?.nodes ?? [];
      const shipToParties = results.map((result) => ({
        label: (
          <div className="custom-single-value-wrapper">
            <h4 className="custom-single-value-wrapper-company-name">{`${result?.name}`}</h4>{' '}
            <p className="custom-single-value-wrapper-other-address-details">
              {`${result?.addressLine1}, ${result?.addressLine2 ? `${result?.addressLine2},` : ''} ${result?.city} - ${
                result?.postcode
              }
              ${result?.state}, ${result?.country}`}
            </p>
          </div>
        ),
        value: result?.id,
        meta: result,
      }));
      return shipToParties;
    }

    return [];
  };

  const loadOptions = React.useCallback(
    debounce((inputText, callback) => {
      loadSoldToAddressOptions(inputText).then((options) => callback(options));
    }, DEBOUNCE_WAIT_TIME),
    []
  );

  return (
    <div
      className="commerce-address-container ppc-search-sold-to-address-container"
      data-testid="ppc-search-sold-to-address-container"
    >
      <Field
        label="Sold-to address"
        data-testid="sold-to-address-field"
        name={fieldName}
        isRequired={!autoSelectedSoldToAddress}
      >
        {() =>
          autoSelectedSoldToAddress ? (
            <div className="selected-commerce-address-container">
              <FormatCommerceAddress commerceAddress={autoSelectedSoldToAddress} />
            </div>
          ) : isAddressSelected && selectedSoldToAddress ? (
            <div className="selected-commerce-address-container">
              <FormatCommerceAddress commerceAddress={selectedSoldToAddress} />
              <Button
                appearance="link"
                testId="ppc-change-sold-to-address"
                onClick={() => {
                  setIsAddressSelected(false);
                }}
              >
                Change
              </Button>
            </div>
          ) : (
            <>
              <AsyncSelect
                isOpen={true}
                classNamePrefix={'ppc-async-search-sold-to-address-select'}
                components={{
                  Input: Input,
                  NoOptionsMessage: NoOptionsMessage,
                  DropdownIndicator: null,
                }}
                placeholder="Search sold-to address"
                isLoading={loading}
                loadOptions={loadOptions}
                isSearchable={true}
                isClearable={true}
                onChange={(selectedAddress) => {
                  const address = ((selectedAddress as unknown) as { meta: Partial<CommerceAddress> }).meta;
                  if (address) {
                    setSelectedSoldToAddress(address);
                    onSelectSoldToAddress && onSelectSoldToAddress(address);
                  }
                  setIsAddressSelected(true);
                }}
              />
              <HelperMessage>Enter sold-to address or sold-to company name</HelperMessage>
            </>
          )
        }
      </Field>
    </div>
  );
};

export const SearchSoldToAddress = withApollo(SearchSoldToAddressImpl);
